import { useMemo } from "react"
import { Box, Button, Stack, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { If, Else } from "lib/@components/switch"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { invalidate } from "lib/graphql/cache"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { getSSOEstateOverview } from "routes/system-admin/sso-overview/controller/get-sso-estate-overview"
import { EstateSummary } from "routes/system-admin/sso-overview/estate-summary"
import { EstateClientTable } from "routes/system-admin/sso-overview/estate-client-table"
import { DomainIndexTable } from "routes/system-admin/sso-overview/domain-index-table"

/**
 * Cross-client SSO / SCIM overview. One GraphQL request backs the whole panel; the search box
 * filters what has already been fetched rather than re-querying, so typing costs nothing.
 */
export function SSOEstatePanel() {
    const overview = getSSOEstateOverview.useResults({}, withInitialValue(IS_LOADING))
    const [search] = useSearchParam("search", "")
    const loading = overview === IS_LOADING
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const clients = useMemo(filterClients, [overview, search])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const domains = useMemo(filterDomains, [overview, search])

    if (loading) return <LoadingScreen description="Identity provider estate" />

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box>
                    <Typography variant="h6">Identity Providers</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                        Every client with an SSO configuration, and the state of their SCIM connection. Use the domain
                        index below to find which organisation owns an email domain when a client is told their domain
                        is already configured.
                    </Typography>
                </Box>
                <Button
                    onClick={refreshOverview}
                    startIcon={<Iconify icon="mdi:refresh" />}
                    data-cy="sso-estate-refresh"
                >
                    Refresh
                </Button>
            </Stack>

            <EstateSummary clients={overview?.clients ?? []} generatedAt={overview?.generatedAt} />

            <SearchBoxUsingSearchParam
                autoFocus={false}
                placeholder="Search by client, provider or email domain..."
                startAdornment={<Iconify icon="mdi:magnify" />}
            />

            <If value={(overview?.clients ?? []).length > 0}>
                <EstateClientTable clients={clients} />
                <DomainIndexTable domains={domains} />
                <Else>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No client has an SSO configuration yet.
                    </Typography>
                </Else>
            </If>
        </Box>
    )

    function refreshOverview() {
        invalidate("ssoEstateOverview")
    }

    function filterClients() {
        const all = overview === IS_LOADING ? [] : overview?.clients ?? []
        if (!search) return all
        return all.filter(matchesClient)
    }

    function filterDomains() {
        const all = overview === IS_LOADING ? [] : overview?.domains ?? []
        if (!search) return all
        return all.filter(matchesDomain)
    }

    function matchesClient(client) {
        return contains([client.clientName, client.clientId, client.providerName, ...(client.emailDomains ?? [])])
    }

    function matchesDomain(domain) {
        return contains([domain.domain, domain.clientName, domain.clientId, domain.providerName])
    }

    function contains(values) {
        const needle = search.toLowerCase()
        return values.filter(Boolean).some((value) => String(value).toLowerCase().includes(needle))
    }
}
